/**
 * Phase 2.5: Full-Screen Dictation Screen
 * 
 * Three-zone layout:
 * LEFT   — Push-to-talk microphone + live waveform + transcript
 * RIGHT  — Passive documentation completeness indicators
 * BOTTOM — Collapsible structured note preview
 * 
 * After dictation: Review → Confirm → Attest → Sign
 */

import { useState } from 'react';
import {
  Mic,
  MicOff,
  Square,
  ArrowRight,
  ArrowLeft,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDictation } from '@/hooks/useDictation';
import { AudioWaveform } from './AudioWaveform';
import { DocumentationCompletenessPanel } from './DocumentationCompletenessPanel';
import { RevenueIntegrityMonitor } from './RevenueIntegrityMonitor';
import { StructuredNotePreview } from './StructuredNotePreview';
import { ProcedureConfirmationPanel } from './ProcedureConfirmationPanel';
import { SurgeonAttestationPanel } from './SurgeonAttestationPanel';
import { formatStructuredNote } from '@/lib/dictation/structuredNoteParser';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface DictationScreenProps {
  authorizationId: string | null;
  caseNumber: string;
  surgeonId: string;
  authorizedCpts: Array<{ code: string; description: string }>;
  embedded?: boolean;
  onComplete?: (data: {
    transcript: string;
    confirmations: Array<{ cptCode: string; status: string }>;
    signedAt: string;
  }) => void;
  onCancel?: () => void;
}

export function DictationScreen({
  authorizationId,
  caseNumber,
  surgeonId,
  authorizedCpts,
  embedded = false,
  onComplete,
  onCancel,
}: DictationScreenProps) {
  const { isDemoMode } = useDemoMode();
  const [showDemoLoaded, setShowDemoLoaded] = useState(false);

  const dictation = useDictation({
    authorizationId,
    caseNumber,
    surgeonId,
    authorizedCpts,
    isDemoMode,
  });

  const handleLoadDemo = () => {
    dictation.loadDemoTranscript();
    setShowDemoLoaded(true);
    toast.info('Demo dictation loaded', { description: 'Sample TKA operative note preloaded.' });
  };

  const handleAutoConfirm = () => {
    dictation.autoConfirmAll();
    toast.info('Demo: All procedures auto-confirmed.');
  };

  const handleFinishAndSign = () => {
    const success = dictation.signNote();
    if (success) {
      toast.success('Operative Note Signed', {
        description: 'Note locked and ready for reconciliation.',
      });
      onComplete?.({
        transcript: dictation.rawTranscript,
        confirmations: dictation.confirmations.map(c => ({
          cptCode: c.cptCode,
          status: c.status,
        })),
        signedAt: new Date().toISOString(),
      });
    }
  };

  // Step header
  const stepLabels: Record<string, string> = {
    dictating: 'Step 1 — Dictate Operative Note',
    reviewing: 'Step 2 — Review Operative Note',
    confirming: 'Step 3 — Confirm Procedures',
    attesting: 'Step 4 — Sign & Lock',
  };

  return (
    <div className={embedded ? "flex flex-col min-h-[70vh]" : "fixed inset-0 z-50 bg-background flex flex-col overflow-hidden"}>
      {/* Top Bar */}
      <div className="shrink-0 border-b border-border/50 bg-card/80 backdrop-blur-xl px-6 py-3">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <div>
              <h1 className="text-sm font-semibold">
                Operative Dictation — Case {caseNumber}
              </h1>
              <p className="text-xs text-muted-foreground">
                {stepLabels[dictation.step]}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isDemoMode && (
              <Badge variant="outline" className="border-warning/50 text-warning text-xs gap-1">
                <AlertTriangle className="w-3 h-3" />
                Demo Mode — No Data Saved
              </Badge>
            )}
            {!dictation.isSigned && (
              <Button variant="ghost" size="sm" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        {dictation.step === 'dictating' && (
          <DictatingView
            dictation={dictation}
            isDemoMode={isDemoMode}
            showDemoLoaded={showDemoLoaded}
            onLoadDemo={handleLoadDemo}
            authorizedCpts={authorizedCpts}
          />
        )}

        {dictation.step === 'reviewing' && (
          <ReviewingView
            dictation={dictation}
            isDemoMode={isDemoMode}
          />
        )}

        {dictation.step === 'confirming' && (
          <ConfirmingView
            dictation={dictation}
            isDemoMode={isDemoMode}
            onAutoConfirm={handleAutoConfirm}
          />
        )}

        {dictation.step === 'attesting' && (
          <AttestingView
            dictation={dictation}
            isDemoMode={isDemoMode}
            onSign={handleFinishAndSign}
          />
        )}
      </div>
    </div>
  );
}

// ============ Sub-views ============

function DictatingView({
  dictation,
  isDemoMode,
  showDemoLoaded,
  onLoadDemo,
  authorizedCpts,
}: {
  dictation: ReturnType<typeof useDictation>;
  isDemoMode: boolean;
  showDemoLoaded: boolean;
  onLoadDemo: () => void;
  authorizedCpts: Array<{ code: string; description: string }>;
}) {
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT — Primary Focus */}
        <div className="flex-1 flex flex-col p-6 overflow-y-auto">
          {/* Microphone Control */}
          <div className="flex flex-col items-center gap-4 mb-6">
            <button
              onClick={dictation.isRecording ? dictation.stopRecording : dictation.startRecording}
              className={cn(
                'w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 border-2',
                dictation.isRecording
                  ? 'bg-destructive/20 border-destructive text-destructive hover:bg-destructive/30 shadow-[0_0_30px_hsl(var(--destructive)/0.3)]'
                  : 'bg-primary/20 border-primary text-primary hover:bg-primary/30 shadow-[0_0_30px_hsl(var(--primary)/0.2)]'
              )}
            >
              {dictation.isRecording ? (
                <Square className="w-8 h-8" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </button>
            <p className="text-xs text-muted-foreground">
              {dictation.isRecording ? 'Recording — Click to stop' : 'Click to start dictation'}
            </p>
            {dictation.recordingError && (
              <Alert className="max-w-md border-warning/30 bg-warning/5 text-left">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <AlertDescription className="text-xs text-warning">
                  {dictation.recordingError}
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Waveform */}
          <AudioWaveform
            audioLevel={dictation.audioLevel}
            isRecording={dictation.isRecording}
            className="mb-6"
          />

          {/* Demo load button */}
          {isDemoMode && !showDemoLoaded && !dictation.rawTranscript && (
            <div className="flex justify-center mb-4">
              <Button variant="outline" size="sm" onClick={onLoadDemo}>
                Load Demo Dictation
              </Button>
            </div>
          )}

          {/* Live Transcript */}
          <div className="flex-1 min-h-[200px]">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Live Transcript
            </h3>
            <div className="bg-muted/20 border border-border/50 rounded-lg p-4 min-h-[200px] max-h-[50vh] overflow-y-auto font-mono text-sm leading-relaxed">
              {dictation.rawTranscript && (
                <span className="text-foreground/90">{dictation.rawTranscript}</span>
              )}
              {dictation.liveFragment && (
                <span className="text-primary/60 italic"> {dictation.liveFragment}</span>
              )}
              {!dictation.rawTranscript && !dictation.liveFragment && (
                <span className="text-muted-foreground/40 italic">
                  Begin dictation to see transcript here…
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {dictation.fullTranscript.split(/\s+/).filter(Boolean).length} words
            </p>
          </div>
        </div>

        {/* RIGHT — Revenue Integrity Monitor */}
        <div className="w-80 shrink-0 border-l border-border/50 p-4 overflow-y-auto bg-card/30">
          <RevenueIntegrityMonitor
            authorizedCpts={authorizedCpts}
            transcript={dictation.fullTranscript}
          />
        </div>
      </div>

      {/* BOTTOM — Structured Preview + Navigation */}
      <div className="shrink-0 border-t border-border/50 p-4 bg-card/50 space-y-3">
        <StructuredNotePreview note={dictation.structuredNote} />

        <div className="flex justify-end">
          <Button
            onClick={dictation.goToReview}
            disabled={!dictation.fullTranscript.trim()}
            className="gap-2"
          >
            Finish & Review
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewingView({
  dictation,
  isDemoMode,
}: {
  dictation: ReturnType<typeof useDictation>;
  isDemoMode: boolean;
}) {
  const formattedNote = formatStructuredNote(dictation.structuredNote);
  const displayNote = formattedNote || dictation.fullTranscript;

  return (
    <div className="h-full flex flex-col p-6 max-w-4xl mx-auto overflow-y-auto">
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Final Operative Note Review</h2>
          <p className="text-sm text-muted-foreground">
            This is the version that will be exported to the EHR. Review for accuracy before proceeding.
          </p>
        </div>

        {isDemoMode && (
          <Alert className="border-warning/30 bg-warning/5">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <AlertDescription className="text-xs text-warning">
              Demo Mode — Illustrative Only — No claims submitted
            </AlertDescription>
          </Alert>
        )}

        {/* Read-only formatted note */}
        <div className="bg-muted/20 border border-border/50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border/30">
            <div>
              <p className="font-semibold">OPERATIVE REPORT</p>
              <p className="text-xs text-muted-foreground">
                Case {dictation.startedAt && format(new Date(dictation.startedAt), 'MMM d, yyyy')}
              </p>
            </div>
            <Badge variant="outline" className="text-xs">Read-Only</Badge>
          </div>
          <pre className="text-sm whitespace-pre-wrap font-mono leading-relaxed text-foreground/90">
            {displayNote}
          </pre>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4">
          <Button variant="outline" onClick={dictation.goBackToDictation} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Re-dictate
          </Button>
          <Button onClick={dictation.goToConfirmation} className="gap-2">
            Confirm Procedures
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmingView({
  dictation,
  isDemoMode,
  onAutoConfirm,
}: {
  dictation: ReturnType<typeof useDictation>;
  isDemoMode: boolean;
  onAutoConfirm: () => void;
}) {
  return (
    <div className="h-full flex flex-col p-6 max-w-3xl mx-auto overflow-y-auto">
      <div className="space-y-4">
        {isDemoMode && (
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={onAutoConfirm}>
              Demo: Auto-Confirm All
            </Button>
          </div>
        )}

        <ProcedureConfirmationPanel
          confirmations={dictation.confirmations}
          onUpdate={dictation.updateConfirmation}
        />

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4">
          <Button variant="outline" onClick={dictation.goBackToReview} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Review
          </Button>
          <Button
            onClick={dictation.goToAttestation}
            disabled={!dictation.allConfirmed}
            className="gap-2"
          >
            Proceed to Sign
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function AttestingView({
  dictation,
  isDemoMode,
  onSign,
}: {
  dictation: ReturnType<typeof useDictation>;
  isDemoMode: boolean;
  onSign: () => void;
}) {
  return (
    <div className="h-full flex flex-col p-6 max-w-3xl mx-auto overflow-y-auto">
      <div className="space-y-4">
        {isDemoMode && (
          <Alert className="border-warning/30 bg-warning/5">
            <AlertTriangle className="w-4 h-4 text-warning" />
            <AlertDescription className="text-xs text-warning">
              Demo Mode — Illustrative Only — No claims submitted
            </AlertDescription>
          </Alert>
        )}

        <SurgeonAttestationPanel
          attested={dictation.attested}
          onAttestedChange={dictation.setAttested}
          onSign={onSign}
          isSigned={dictation.isSigned}
          attestedAt={dictation.attestedAt}
          confirmations={dictation.confirmations}
        />

        {/* Navigation */}
        {!dictation.isSigned && (
          <div className="flex items-center pt-4">
            <Button variant="outline" onClick={() => dictation.setStep('confirming')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Confirmations
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
